import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { AvatarModule } from 'primeng/avatar';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { MenuModule } from 'primeng/menu';
import { filter, Subscription } from 'rxjs';

import { MENU_ITEMS } from '../core/config/menu.config';
import { MenuItem } from '../core/models/menu.model';
import { AuthService } from '../core/auth/services/auth.service';
import { TokenService } from '../core/auth/services/token.service';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-shell',
  standalone: true,
  imports: [CommonModule, RouterOutlet, RouterLink, RouterLinkActive, ButtonModule, AvatarModule, TooltipModule, MenuModule, TranslateModule],
  styles: [
    ':host { display: block; }',
    '.menu-link-active { background-color: rgba(232, 160, 176, 0.15); color: #fff; box-shadow: inset 3px 0 0 #e8a0b0; }',
    '.menu-group-toggle { width: 100%; }',
    ':host ::ng-deep .menu-tooltip { border: 1px solid #071820; box-shadow: 0 8px 20px rgba(0, 0, 0, 0.35); }',
    ':host ::ng-deep .menu-tooltip .p-tooltip-text { background-color: #071820; color: #fff; opacity: 1; padding: 10px 12px; border-radius: 10px; font-weight: 600; letter-spacing: 0.01em; }',
    ':host ::ng-deep .menu-tooltip .p-tooltip-arrow { display: none; }',
    ':host ::ng-deep .avatar-menu .p-menu-list { padding: 0.5rem 0; }',
    ':host ::ng-deep .avatar-menu .p-menuitem-link { padding: 0.75rem 1rem; }',
    ':host ::ng-deep .avatar-menu .p-menuitem-text, :host ::ng-deep .avatar-menu .p-menuitem-icon { color: #dc2626 !important; }'
  ],
  template: `
    <div class="flex min-h-screen bg-[var(--bg-soft)] text-slate-800 relative">
      <div
        class="fixed inset-0 bg-black/30 backdrop-blur-sm z-30 lg:hidden"
        *ngIf="!isDesktop && mobileOpen"
        (click)="toggleSidebar()"
        aria-hidden="true"
      ></div>

      <aside
        class="text-white flex flex-col fixed inset-y-0 left-0 z-40 transform transition-transform duration-200"
        style="background: linear-gradient(180deg, #071820 0%, #0f2c3a 60%, #113d4a 100%)"
        [class.-translate-x-full]="!isDesktop && !mobileOpen"
        [style.width.px]="asideWidth"
      >
        <div class="flex items-center gap-3 px-4 py-4 border-b border-white/10" [class.justify-center]="collapseCompact">
          <div class="flex items-center justify-center flex-shrink-0 bg-white rounded-xl overflow-hidden" style="width:2.5rem;height:2.5rem;padding:3px">
            <img src="assets/logo.png" class="w-full h-full object-contain" alt="Psyco Center" />
          </div>
          <div class="leading-tight min-w-0" *ngIf="!collapseCompact">
            <p class="text-[10px] uppercase tracking-widest font-medium" style="color: rgba(232,160,176,0.85)">Psyco Center</p>
            <p class="font-bold text-sm text-white truncate">{{ clinic }}</p>
          </div>
        </div>
        <nav class="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          <ng-container *ngFor="let item of menu; trackBy: trackByLabel">
          <a
              *ngIf="!hasChildren(item); else menuGroup"
              [routerLink]="item.path"
              routerLinkActive="menu-link-active"
              class="flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition hover:bg-white/10 hover:text-white"
              (click)="handleNavClick()"
              pTooltip="{{ item.label }}"
              tooltipStyleClass="menu-tooltip"
              tooltipPosition="right"
              [tooltipDisabled]="!collapseCompact"
            >
              <i [class]="item.icon"></i>
              <span class="flex-1" *ngIf="!collapseCompact">{{ item.label }}</span>
              <span *ngIf="item.badge" class="text-xs bg-white/20 px-2 py-0.5 rounded-full">{{ item.badge }}</span>
            </a>

            <ng-template #menuGroup>
              <button
                type="button"
                class="menu-group-toggle flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition hover:bg-white/10 hover:text-white text-left"
                [class.menu-link-active]="isItemActive(item)"
                (click)="toggleMenuGroup(item)"
                pTooltip="{{ item.label }}"
                tooltipStyleClass="menu-tooltip"
                tooltipPosition="right"
                [tooltipDisabled]="!collapseCompact"
              >
                <i [class]="item.icon"></i>
                <span class="flex-1" *ngIf="!collapseCompact">{{ item.label }}</span>
                <i
                  *ngIf="!collapseCompact"
                  class="pi text-xs text-white/70"
                  [class.pi-chevron-down]="!isMenuGroupExpanded(item)"
                  [class.pi-chevron-up]="isMenuGroupExpanded(item)"
                ></i>
              </button>

              <div
                *ngIf="!collapseCompact && isMenuGroupExpanded(item)"
                class="ml-4 mt-1 space-y-1 border-l border-white/10 pl-3"
              >
                <a
                  *ngFor="let child of item.children; trackBy: trackByLabel"
                  [routerLink]="child.path"
                  routerLinkActive="menu-link-active"
                  class="flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition hover:bg-white/10 hover:text-white"
                  (click)="handleNavClick()"
                >
                  <i [class]="child.icon || 'pi pi-angle-right'"></i>
                  <span class="flex-1">{{ child.label }}</span>
                  <span *ngIf="child.badge" class="text-xs bg-white/20 px-2 py-0.5 rounded-full">{{ child.badge }}</span>
                </a>
              </div>
            </ng-template>
          </ng-container>
        </nav>
        <div class="px-4 py-4 border-t border-white/10 flex items-center gap-3" *ngIf="!collapseCompact; else compactUser">
          <p-avatar [label]="userInitials" shape="circle" styleClass="bg-white/10"></p-avatar>
          <div class="text-sm leading-tight flex-1">
            <p class="font-semibold">{{ user.name }}</p>
            <p class="text-white/70">{{ user.role }}</p>
          </div>
        </div>
        <button
          pButton
          type="button"
          icon="pi pi-sign-out"
          class="p-button-text p-button-sm w-full text-white/80 hover:text-white"
          [class.justify-center]="collapseCompact"
          [label]="collapseCompact ? '' : ('actions.logout' | translate)"
          pTooltip="{{ 'actions.logout' | translate }}"
          tooltipStyleClass="menu-tooltip"
          tooltipPosition="right"
          [tooltipDisabled]="!collapseCompact"
          (click)="logout()"
        ></button>
        <div class="px-4 pb-4 pt-2 border-t border-white/10 text-[11px] leading-4 text-white/65" *ngIf="!collapseCompact">
          <p>© {{ currentYear }} Psyco Center</p>
          <p>
            Desarrollado por
            <a
              href="https://simplified.com.co"
              target="_blank"
              rel="noopener noreferrer"
              class="text-white font-semibold hover:text-sky-200 transition"
            >simplified.com.co</a>
          </p>
        </div>
        <ng-template #compactUser>
          <div class="px-4 py-4 border-t border-white/10 flex items-center justify-center">
            <p-avatar [label]="userInitials" shape="circle" styleClass="bg-white/10"></p-avatar>
          </div>
        </ng-template>
      </aside>

      <div class="flex-1 flex flex-col min-w-0 overflow-hidden" [style.marginLeft.px]="contentMargin">
        <header class="h-14 sm:h-16 bg-white border-b-2 border-[var(--border)] px-2 sm:px-5 lg:px-6 flex items-center justify-between gap-2 sm:gap-3 shadow-md" style="border-bottom-color: #d0daea">
          <div class="flex items-center gap-1 sm:gap-3 text-xs sm:text-sm text-slate-600 min-w-0 flex-1">
            <button
              pButton
              type="button"
              class="p-button-text p-button-rounded p-button-sm"
              icon="pi pi-bars"
              (click)="toggleSidebar()"
              [attr.aria-expanded]="toggleExpanded"
              aria-label="Alternar menú"
            ></button>
            <span class="text-brand-700 font-semibold truncate">{{ currentPageLabel }}</span>
          </div>
          <div class="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            <p-avatar [label]="userInitials" shape="circle" styleClass="shadow-sm cursor-pointer hover:ring-2 hover:ring-primary/20 transition" (click)="profileMenuTop.toggle($event)"></p-avatar>
            <p-menu #profileMenuTop [model]="profileMenuItems" [popup]="true" appendTo="body" styleClass="avatar-menu shadow-lg rounded-xl border-none w-48 mt-2"></p-menu>
          </div>
        </header>

        <main class="p-3 sm:p-4 md:p-6">
          <router-outlet></router-outlet>
        </main>
      </div>
    </div>
  `
})
export class ShellComponent implements OnInit, OnDestroy {
  get menu(): MenuItem[] {
    return this.filterMenuItems(MENU_ITEMS);
  }
  readonly clinic = 'Psyco Center';
  readonly currentYear = new Date().getFullYear();
  user = { name: 'Usuario', role: 'Usuario' };
  isDesktop = typeof window !== 'undefined' ? window.matchMedia('(min-width: 1024px)').matches : true;
  sidebarCollapsed = false;
  mobileOpen = false;
  currentPageLabel = 'Dashboard';

  private authService = inject(AuthService);
  private tokenService = inject(TokenService);

  profileMenuItems = [
    {
      label: 'Cerrar Sesión',
      icon: 'pi pi-sign-out',
      command: () => {
        this.logout();
      }
    }
  ];

  private mediaQuery: MediaQueryList | null = typeof window !== 'undefined' ? window.matchMedia('(min-width: 1024px)') : null;
  private mediaListener?: (event: MediaQueryListEvent) => void;
  private routerSub?: Subscription;
  private expandedGroups = new Set<string>();

  constructor(private readonly router: Router) { }

  get userInitials() {
    return this.buildInitials(this.user.name);
  }

  trackByLabel(_index: number, item: MenuItem) {
    return item.label;
  }

  hasChildren(item: MenuItem) {
    return Boolean(item.children?.length);
  }

  isMenuGroupExpanded(item: MenuItem) {
    return this.expandedGroups.has(item.label) || this.isItemActive(item);
  }

  isItemActive(item: MenuItem) {
    return this.matchesCurrentRoute(item);
  }

  toggleSidebar() {
    if (this.isDesktop) {
      this.sidebarCollapsed = !this.sidebarCollapsed;
    } else {
      this.mobileOpen = !this.mobileOpen;
    }
  }

  toggleMenuGroup(item: MenuItem) {
    if (!this.hasChildren(item)) {
      return;
    }

    if (this.collapseCompact) {
      this.sidebarCollapsed = false;
      return;
    }

    if (this.expandedGroups.has(item.label)) {
      this.expandedGroups.delete(item.label);
      return;
    }

    this.expandedGroups.add(item.label);
  }

  handleNavClick() {
    if (!this.isDesktop) {
      this.mobileOpen = false;
    }
  }

  ngOnInit() {
    if (this.mediaQuery) {
      this.mediaListener = (event: MediaQueryListEvent) => {
        this.isDesktop = event.matches;
        if (event.matches) {
          this.mobileOpen = false;
        } else {
          this.sidebarCollapsed = false;
        }
      };
      this.mediaQuery.addEventListener('change', this.mediaListener);
    }

    const payload = this.tokenService.decodeToken();
    if (payload) {
      const resolvedName = payload.full_name || payload.name || payload.preferred_username || payload.username || 'Usuario';
      this.user = {
        name: resolvedName,
        role: this.formatRoleLabel(payload.roles)
      };
    }

    this.updateBreadcrumbFromUrl(this.router.url);
    this.routerSub = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => this.updateBreadcrumbFromUrl(event.urlAfterRedirects));
  }

  ngOnDestroy() {
    if (this.mediaQuery && this.mediaListener) {
      this.mediaQuery.removeEventListener('change', this.mediaListener);
    }
    this.routerSub?.unsubscribe();
  }

  logout() {
    this.authService.logout();
  }

  private updateBreadcrumbFromUrl(url: string) {
    const segment = url.split('/').filter(Boolean)[0] ?? 'equipos';
    const trail = this.findMenuTrail(this.menu, segment);
    this.currentPageLabel = trail?.map((item) => item.label).join(' / ') ?? 'Equipos';

    trail?.slice(0, -1).forEach((item) => this.expandedGroups.add(item.label));
  }

  get collapseCompact() {
    return this.isDesktop && this.sidebarCollapsed;
  }

  get asideWidth() {
    if (this.isDesktop) {
      return this.sidebarCollapsed ? 72 : 256;
    }
    return 256;
  }

  get contentMargin() {
    if (this.isDesktop) {
      return this.sidebarCollapsed ? 72 : 256;
    }
    return 0;
  }

  get toggleExpanded() {
    return this.isDesktop ? !this.sidebarCollapsed : this.mobileOpen;
  }

  private buildInitials(name: string) {
    const parts = name
      .trim()
      .split(/\s+/)
      .filter(Boolean);

    if (parts.length === 0) {
      return 'US';
    }

    if (parts.length === 1) {
      return parts[0].slice(0, 2).toUpperCase();
    }

    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }

  private formatRoleLabel(roles: unknown): string {
    if (Array.isArray(roles) && roles.length > 0) {
      return roles
        .map((role) => String(role).replace(/^ROLE_/, ''))
        .join(', ');
    }

    if (typeof roles === 'string' && roles.trim().length > 0) {
      return roles.replace(/^ROLE_/, '');
    }

    return 'Usuario';
  }

  private filterMenuItems(items: MenuItem[]): MenuItem[] {
    return items
      .map((item) => {
        const children = item.children ? this.filterMenuItems(item.children) : undefined;
        return { ...item, children };
      })
      .filter((item) => {
        const hasPermission = !item.permission || this.tokenService.hasPermission(item.permission);
        return hasPermission && (Boolean(item.path) || Boolean(item.children?.length));
      });
  }

  private matchesCurrentRoute(item: MenuItem): boolean {
    const currentPath = this.normalizePath(this.router.url);

    if (item.path && currentPath === this.normalizePath(item.path)) {
      return true;
    }

    return item.children?.some((child) => this.matchesCurrentRoute(child)) ?? false;
  }

  private findMenuTrail(items: MenuItem[], segment: string): MenuItem[] | null {
    for (const item of items) {
      if ((item.path ?? '').replace('/', '') === segment) {
        return [item];
      }

      if (item.children?.length) {
        const childTrail = this.findMenuTrail(item.children, segment);
        if (childTrail) {
          return [item, ...childTrail];
        }
      }
    }

    return null;
  }

  private normalizePath(path: string): string {
    const normalizedPath = path.split('?')[0].split('#')[0] || '/';
    return normalizedPath.startsWith('/') ? normalizedPath : `/${normalizedPath}`;
  }
}
